/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import React, { useEffect, useState } from 'react';
import { authenticatedFetch } from '@shared/lib/api';
import type { ApiResponse } from '@shared/types/api';
import type { MeResponseData } from '@shared/types/responses/auth';
import { isElectronApp } from '@shared/utils/env';
import { SkillShopService } from '../services/skillShopService';

interface AuthCheckProps {
  children: React.ReactNode;
}

type AuthResponse = ApiResponse<MeResponseData>;

export function AuthCheck({ children }: AuthCheckProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [skillShopService] = useState(() => new SkillShopService());

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      // In development mode, bypass authentication
      if (import.meta.env.DEV) {
        console.log('[AuthCheck] Development mode detected - bypassing authentication');
        setHasAccess(true);
        setIsLoading(false);
        return;
      }

      const isElectron = isElectronApp();
      
      if (isElectron) {
        // ELECTRON MODE: Simple auth check
        // In Electron, the server doesn't require JWT tokens.
        // It loads the logged-in user directly from the database.
        // We just need to check if a user exists.
        console.log('[AuthCheck] Electron mode - checking for logged-in user');
        
        const result = await skillShopService.checkElectronAuth();
        
        if (result.success && result.user) {
          console.log('[AuthCheck] User authenticated:', result.user.email);
          setHasAccess(true);
          setIsLoading(false);
          return;
        }
        
        // No user logged in - redirect to signin
        console.log('[AuthCheck] No logged-in user found - redirecting to signin');
        const signinPath = `${import.meta.env.BASE_URL}signin`.replace(/\/+/g, '/');
        window.location.href = signinPath;
        return;
      }
      
      // WEB MODE: Standard JWT authentication
      const token = localStorage.getItem('accessToken');
      
      if (!token) {
        console.log('[AuthCheck] No access token found - redirecting to signin');
        const signinPath = `${import.meta.env.BASE_URL}signin`.replace(/\/+/g, '/');
        window.location.href = signinPath;
        return;
      }

      // Use standardized authenticated fetch to validate the session
      // This will automatically try to refresh the token if it's expired
      const response = await authenticatedFetch('/api/auth/me');

      if (response.status === 401) {
        console.log('[AuthCheck] Authentication failed - redirecting to signin');
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        const signinPath = `${import.meta.env.BASE_URL}signin`.replace(/\/+/g, '/');
        window.location.href = signinPath;
        return;
      }

      let result: AuthResponse | null = null;

      try {
        result = await response.json();
      } catch (parseError) {
        console.error('Failed to parse auth response:', parseError);
      }

      if (response.ok && result?.success && result.data) {
        setHasAccess(true);
      } else {
        const errorMessage =
          result?.error?.message ||
          'Unable to verify access at this time. Please try refreshing the page.';
        setError(errorMessage);
      }
    } catch (err) {
      console.error('Auth check failed:', err);
      setError('Unable to verify access. Please try refreshing the page.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoginRedirect = () => {
    // Redirect to signin page using base URL
    const signinPath = `${import.meta.env.BASE_URL}signin`.replace(/\/+/g, '/');
    window.location.href = signinPath;
  };

  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '4px solid #f3f3f3',
            borderTop: '4px solid #0066cc',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }} />
          <div style={{ color: '#666' }}>Checking access...</div>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        backgroundColor: '#f8f9fa'
      }}>
        <div style={{
          maxWidth: '500px',
          padding: '40px',
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          textAlign: 'center'
        }}>
          <div style={{
            width: '64px',
            height: '64px',
            backgroundColor: '#dc3545',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px',
            color: 'white',
            fontSize: '24px'
          }}>
            ⚠️
          </div>
          
          <h2 style={{
            margin: '0 0 16px 0',
            color: '#333',
            fontSize: '24px'
          }}>
            Access Denied
          </h2>
          
          <p style={{
            color: '#666',
            marginBottom: '32px',
            lineHeight: '1.5'
          }}>
            {error}
          </p>
          
          <button
            onClick={handleLoginRedirect}
            style={{
              backgroundColor: '#0066cc',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '6px',
              fontSize: '16px',
              cursor: 'pointer',
              fontWeight: '500'
            }}
            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#0052a3'}
            onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#0066cc'}
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}